import {
  Brush,
  Circle,
  Crop,
  Eraser,
  type LucideIcon,
  Minus,
  MousePointer2,
  MoveUpRight,
  PaintBucket,
  Pencil,
  Pipette,
  Search,
  Square,
  Type,
} from 'lucide-react';
import { TOOLS, type ToolId } from '../core/tools';
import { useEditorStore } from '../state/editorStore';
import { IconButton } from './components/controls';

/**
 * Tool rail. Icons come from lucide (MIT, tree-shaken per icon). Tools that are
 * not wired yet are rendered disabled with their target iteration, so the rail
 * doubles as a visible roadmap.
 */
const ICONS: Record<ToolId, LucideIcon> = {
  move: MousePointer2,
  crop: Crop,
  brush: Brush,
  pencil: Pencil,
  eraser: Eraser,
  line: Minus,
  rect: Square,
  ellipse: Circle,
  arrow: MoveUpRight,
  text: Type,
  fill: PaintBucket,
  eyedropper: Pipette,
  zoom: Search,
};

export function ToolRail() {
  const activeTool = useEditorStore((s) => s.activeTool);
  const setTool = useEditorStore((s) => s.setTool);

  return (
    <nav
      aria-label="Tools"
      className="flex w-11 shrink-0 flex-col items-center gap-0.5 border-r border-line bg-surface py-1.5"
      data-testid="toolrail"
    >
      {TOOLS.map((tool) => {
        const Icon = ICONS[tool.id];
        const disabled = tool.plannedFor !== undefined;
        const label = disabled
          ? `${tool.label} — planned for Iteration ${tool.plannedFor}`
          : `${tool.label} (${tool.key.toUpperCase()})`;
        return (
          <IconButton
            key={tool.id}
            label={label}
            active={activeTool === tool.id}
            disabled={disabled}
            onClick={() => setTool(tool.id)}
            data-testid={`tool-${tool.id}`}
          >
            <Icon size={16} />
          </IconButton>
        );
      })}
    </nav>
  );
}
